/**
 * Student Form Dialog Component
 *
 * Modal dialog for creating and editing students.
 * Supports both create and edit modes.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Stack,
} from '@mui/material';
import {
  useCreateStudentMutation,
  useUpdateStudentMutation,
} from '../../../api/studentsApi';
import type { Student, StudentFormData } from '../../../types/students';

interface StudentFormDialogProps {
  open: boolean;
  onClose: () => void;
  studioId: string;
  student?: Student | null;
  mode: 'create' | 'edit';
}

const StudentFormDialog: React.FC<StudentFormDialogProps> = ({
  open,
  onClose,
  studioId,
  student,
  mode,
}) => {
  // RTK Query mutations
  const [createStudent, { isLoading: isCreating }] = useCreateStudentMutation();
  const [updateStudent, { isLoading: isUpdating }] = useUpdateStudentMutation();

  // Form state
  const [formData, setFormData] = useState<StudentFormData>({
    location: '',
    first_name: '',
    last_name: '',
    phone: '',
    classes: '',
    gender: '',
    age: null,
    birthday: null,
  });

  const [error, setError] = useState<string | null>(null);

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && student) {
        setFormData({
          location: student.location || '',
          first_name: student.first_name || '',
          last_name: student.last_name || '',
          phone: student.phone || '',
          classes: student.classes || '',
          gender: student.gender || '',
          age: student.age,
          birthday: student.birthday,
        });
      } else {
        // Reset form for create mode
        setFormData({
          location: '',
          first_name: '',
          last_name: '',
          phone: '',
          classes: '',
          gender: '',
          age: null,
          birthday: null,
        });
      }
      setError(null);
    }
  }, [open, mode, student]);

  // Handle form field changes
  const handleChange = (field: keyof StudentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      if (mode === 'create') {
        await createStudent({
          studioId,
          data: { ...formData, studio: studioId },
        }).unwrap();
      } else if (student) {
        await updateStudent({
          studioId,
          studentId: student.id,
          data: { ...formData, studio: studioId },
        }).unwrap();
      }
      onClose();
    } catch (err: any) {
      // Try to extract a meaningful error message
      let errorMessage = 'Failed to save student. Please try again.';
      if (err?.data) {
        if (typeof err.data === 'string') {
          errorMessage = err.data;
        } else if (err.data.message) {
          errorMessage = err.data.message;
        } else if (err.data.error) {
          errorMessage = err.data.error;
        } else {
          // Show all validation errors if available
          errorMessage = JSON.stringify(err.data, null, 2);
        }
      }

      setError(errorMessage);
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Add New Student' : 'Edit Student'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.first_name}
              onChange={e => handleChange('first_name', e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="Last Name"
              value={formData.last_name}
              onChange={e => handleChange('last_name', e.target.value)}
              required
            />
          </Box>
          <TextField
            fullWidth
            label="Location"
            value={formData.location}
            onChange={e => handleChange('location', e.target.value)}
          />
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={e => handleChange('phone', e.target.value)}
            />
            <TextField
              fullWidth
              select
              label="Gender"
              value={formData.gender}
              onChange={e => handleChange('gender', e.target.value)}
            >
              <MenuItem value="M">Male</MenuItem>
              <MenuItem value="F">Female</MenuItem>
              <MenuItem value="O">Other</MenuItem>
            </TextField>
          </Box>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              type="number"
              label="Age"
              value={formData.age || ''}
              onChange={e =>
                handleChange(
                  'age',
                  e.target.value ? Number(e.target.value) : null
                )
              }
            />
            <TextField
              fullWidth
              type="date"
              label="Birthday"
              value={formData.birthday || ''}
              onChange={e => handleChange('birthday', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Classes"
            value={formData.classes}
            onChange={e => handleChange('classes', e.target.value)}
            placeholder="Enter class names, one per line or comma-separated"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !formData.first_name || !formData.last_name}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentFormDialog;
